import React from 'react';
import Swal from 'sweetalert2';
import { settings } from '../../../settings';
import Fetch from '../../../utils/fetch';
import TextArea from '../../../components/inputs/TextArea';
import Button from '../../../components/buttons/Button';
import EditableAddress from './editOrderComponents/EditableAddress';
import GeneralInfo from './editOrderComponents/GeneralInfo';
import PinCodes from './editOrderComponents/PinCodes';
import EditDeliveryFromUntilDate from './editOrderComponents/EditDeliveryFromUntilDate';
import { EditPackageInfo } from './editOrderComponents/EditPackageInfo';
import {
  EditAddress,
  FormOrderDeliveryType,
  OrderForm,
  orderFormToUpdateRequest,
} from './editOrderComponents/transformToEditOrderRequestObject';
import { Order, OrderDeliveryType, Region } from '../../../types/orderTypes';
import { packageSize } from '../../orders/OrdersUtil';
import dayjs from 'dayjs';
import { getHubConfig } from '../../../const/HubConfig';

const DELIVERY_TYPE: { [key in OrderDeliveryType]: FormOrderDeliveryType } = {
  EXPRESS: 'Express',
  SCHEDULED: 'Scheduled',
};

interface Props {
  newOrder: Order;
  region: Region;
  bundleId?: string;
}

interface State {
  orderForm: OrderForm;
  updatingOrder: boolean;
  orderToUpdate: Order;
}

class EditOrderContent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      orderForm: {
        sender: {
          name: props.newOrder.pickupName,
          email: props.newOrder.pickupEmail,
          areaCode: props.newOrder.pickupPhoneCountryCode,
          phone: props.newOrder.pickupPhoneNumber,
          streetName: props.newOrder.originAddress.streetName,
          streetNumber: props.newOrder.originAddress.streetNumber,
          postalCode: props.newOrder.originAddress.postalCode,
          city: props.newOrder.originAddress.city,
          country: props.newOrder.originAddress.country,
        },
        recipient: {
          name: props.newOrder.recipientName,
          email: props.newOrder.recipientEmail,
          areaCode: props.newOrder.recipientPhoneCountryCode,
          phone: props.newOrder.recipientPhoneNumber,
          streetName: props.newOrder.recipientStreetName,
          streetNumber: props.newOrder.recipientStreetNumber,
          postalCode: props.newOrder.recipientPostalCode,
          city: props.newOrder.recipientCity,
          country: props.newOrder.recipientCountry,
        },
        warehouse: {
          name: props.newOrder.pickupName,
          email: props.newOrder.pickupEmail,
          areaCode: props.newOrder.pickupPhoneCountryCode,
          phone: props.newOrder.pickupPhoneNumber,
          streetName: props.newOrder.warehouseAddress.streetName,
          streetNumber: props.newOrder.warehouseAddress.streetNumber,
          postalCode: props.newOrder.warehouseAddress.postalCode,
          city: props.newOrder.warehouseAddress.city,
          country: props.newOrder.warehouseAddress.country,
        },
        deliveryFromDate: props.newOrder.deliverFrom,
        deliveryUntilDate: props.newOrder.deliverUntil,
        packageSize: packageSize(props.newOrder),
        packageCount: props.newOrder.packagesNumber,
        type: DELIVERY_TYPE[props.newOrder.deliveryType],
        noteForCourier: props.newOrder.noteForCourier,
      },
      updatingOrder: false,
      orderToUpdate: { ...props.newOrder },
    };
  }

  updateOrder = async () => {
    if (this.state.orderToUpdate.id !== this.props.newOrder.id) {
      await Swal.fire({
        title: 'Order ID mismatch!',
        text: 'The order id appears to have changed in the background. Please reload the page and try applying your changes again!',
        icon: 'error',
      });
      window.location.reload();
      return;
    }

    const deliveryDateModified = !dayjs(
      this.state.orderForm.deliveryFromDate
    ).isSame(dayjs(this.props.newOrder.deliverFrom), 'day');
    const orderInBundle = !!this.props.bundleId;

    if (orderInBundle && deliveryDateModified) {
      const warningResult = await Swal.fire({
        title: 'Order is already in a bundle',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'No! Ooops...',
        confirmButtonText: 'Yes! change date of order',
        allowOutsideClick: false,
        focusCancel: true,
        html: `<div>Be careful when changing the delivery date of an order that is already in a bundle.<br /><br />Are you really sure you want to change the date?</div>`,
      });
      if (
        warningResult.dismiss === Swal.DismissReason.cancel ||
        warningResult.dismiss === Swal.DismissReason.backdrop ||
        warningResult.dismiss === Swal.DismissReason.esc
      ) {
        return;
      }
    }

    const packageSizeModified =
      this.state.orderForm.packageSize !== this.props.newOrder.packageSize;
    const packageSizeWarning = packageSizeModified
      ? `<p style="color:#dd4e50">Changing the <strong style="font-weight:bold">Package size</strong> will cause losing the original parcels size details.</p>`
      : '';

    const swalResponse = await Swal.fire({
      html: `${packageSizeWarning}Are you sure you want to update order ${this.props.newOrder.id}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Update order',
      confirmButtonColor: '#2878c1',
      cancelButtonColor: '#ccc',
    });

    if (swalResponse.value) {
      this.setState({ updatingOrder: true });
      const response = await this.sendUpdateOrder(
        this.state.orderToUpdate,
        this.state.orderForm
      );
      this.setState({ updatingOrder: false });

      if (response.ok) {
        await Swal.fire(
          'Success!',
          `Order ${this.props.newOrder.id} has been updated`,
          'success'
        );

        window.location.reload();
      } else {
        const errorMessages = response.errorMessages
          .map(
            item =>
              `<div style="text-align: left; font-size: 1.5rem; margin-bottom: 5px;">Field: ${item.field}<br />Message: ${item.message}</div>`
          )
          .join('');
        const html = `<div>Order ${this.props.newOrder.id} could not be updated<br /><br />${errorMessages}</div>`;

        Swal.fire({
          title: 'Failure!',
          icon: 'warning',
          html,
        });
      }
    }
  };

  sendUpdateOrder = async (
    order: Order,
    orderForm: OrderForm
  ): Promise<{
    ok: boolean;
    errorMessages: { field: string; message: string }[];
  }> => {
    const response = await Fetch.put({
      url: `${settings.newApiUrl}/order/${order.id}`,

      body: orderFormToUpdateRequest(order, orderForm),
    });

    if (response.ok) {
      return { ok: true, errorMessages: [] };
    } else if (response.status >= 500) {
      return { ok: false, errorMessages: [] };
    } else {
      const json = await response.json();
      const errorMessages = json.map(item => {
        return { field: item.propertyPath, message: item.message };
      });
      return { ok: false, errorMessages: errorMessages };
    }
  };

  updateSender = (newData: Partial<EditAddress>) => {
    this.setState({
      orderForm: {
        ...this.state.orderForm,
        sender: {
          ...this.state.orderForm.sender,
          ...newData,
        },
      },
    });
  };

  updateRecipient = (newData: Partial<EditAddress>) => {
    this.setState({
      orderForm: {
        ...this.state.orderForm,
        recipient: {
          ...this.state.orderForm.recipient,
          ...newData,
        },
      },
    });
  };

  updateOrderForm = (newData: Partial<OrderForm>) => {
    this.setState({
      orderForm: {
        ...this.state.orderForm,
        ...newData,
      },
    });
  };

  defaultTextRows = (text: string): number => {
    const lines = text.split('\n').length;
    return Math.max(2, Math.min(lines, 8));
  };

  render() {
    const { bundleId, newOrder } = this.props;
    const { orderForm, updatingOrder } = this.state;

    const noteForCourierRows = this.defaultTextRows(
      orderForm.noteForCourier || ''
    );

    return (
      <div>
        <div className="order__information">
          <div className="information__box">
            <GeneralInfo
              orderId={newOrder.id}
              bundleId={bundleId}
              courier={newOrder.courierName}
            />

            <div style={{ height: 25 }} />

            <EditDeliveryFromUntilDate
              deliveryFromDate={orderForm.deliveryFromDate}
              deliveryUntilDate={orderForm.deliveryUntilDate}
              updateOrderForm={newData => {
                this.updateOrderForm(newData);
              }}
            />
          </div>

          <EditPackageInfo
            orderForm={orderForm}
            updateOrderForm={newData => {
              this.updateOrderForm(newData);
            }}
          />

          <PinCodes
            pickupPin={newOrder.pickupPin}
            deliveryPin={newOrder.confirmationPin}
          />
        </div>
        <div style={{ height: 25 }} />
        <div className="order__information">
          <EditableAddress
            header="Sender"
            name={orderForm.sender.name}
            email={orderForm.sender.email}
            streetName={orderForm.sender.streetName}
            streetNumber={orderForm.sender.streetNumber}
            postalCode={orderForm.sender.postalCode}
            city={orderForm.sender.city}
            country={orderForm.sender.country}
            areaCode={orderForm.sender.areaCode}
            phone={orderForm.sender.phone}
            updateAddress={sender => this.updateSender(sender)}
            hubAddress={getHubConfig(this.props.region).hubAddress}
          />

          <EditableAddress
            header="Recipient"
            name={orderForm.recipient.name}
            email={orderForm.recipient.email}
            streetName={orderForm.recipient.streetName}
            streetNumber={orderForm.recipient.streetNumber}
            postalCode={orderForm.recipient.postalCode}
            city={orderForm.recipient.city}
            country={orderForm.recipient.country}
            areaCode={orderForm.recipient.areaCode}
            phone={orderForm.recipient.phone}
            updateAddress={recipient => this.updateRecipient(recipient)}
          />
        </div>
        <div style={{ height: 25 }} />
        Notes for courier:
        <TextArea
          rows={noteForCourierRows}
          value={orderForm.noteForCourier || ''}
          onChange={event =>
            this.updateOrderForm({ noteForCourier: event.target.value })
          }
        />
        <div style={{ height: 25 }} />
        <Button
          title="Update order"
          disabled={updatingOrder}
          onClick={this.updateOrder}
        />
        <div style={{ height: 15 }} />
      </div>
    );
  }
}

export default EditOrderContent;
